/* Shared Action Center activity classification helpers. */
(function () {
  function clean(value) {
    return String(value || "").trim();
  }

  function norm(value) {
    return clean(value).toLowerCase();
  }

  function activityToolKey(activity) {
    const raw = norm(activity && (activity.toolName || activity.messageType || activity.name));
    if (!raw) return "";
    return raw.replace(/^mcp__.*__/, "").split(/[./]/).pop();
  }

  function toolActionMode(toolKey) {
    if (!toolKey) return "";
    if (/(delete|detach|unlink|remove|cancel)/.test(toolKey)) return "delete";
    if (/(attach|link)/.test(toolKey)) return "link";
    if (/(reorder|move|phase_update|status_update|convert)/.test(toolKey)) return "move";
    if (/(queue|run|generate|finalize|mark_paid)/.test(toolKey)) return "run";
    if (/(create|add|upload|ingest|reply)/.test(toolKey)) return "create";
    if (/(update|save|write)/.test(toolKey)) return "write";
    if (/(get|list|export|read|search|summary)/.test(toolKey)) return "read";
    return "work";
  }

  function toolActionLabel(toolKey, mode) {
    if (toolKey.includes("_list")) return "List";
    if (toolKey.includes("_search")) return "Search";
    if (toolKey.includes("_get")) return "Read";
    if (toolKey.includes("_reply")) return "Reply";
    if (toolKey.includes("_attach")) return "Attach";
    if (toolKey.includes("_detach")) return "Detach";
    if (toolKey.includes("_unlink")) return "Unlink";
    if (toolKey.includes("_link")) return "Link";
    if (toolKey.includes("_upload")) return "Upload";
    if (toolKey.includes("_ingest")) return "Ingest";
    if (toolKey.includes("_queue")) return "Queue";
    if (toolKey.includes("_cancel")) return "Cancel";
    if (toolKey.includes("_finalize")) return "Finalize";
    if (toolKey.includes("_mark_paid")) return "Paid";
    if (toolKey.includes("_generate")) return "Generate";
    if (toolKey.includes("_convert")) return "Convert";
    if (toolKey.includes("_move")) return "Move";
    if (toolKey.includes("_reorder")) return "Reorder";
    if (toolKey.includes("_delete") || toolKey.includes("_remove")) return "Delete";
    if (toolKey.includes("_create") || toolKey.includes("_add")) return "Create";
    if (toolKey.includes("_update") || toolKey.includes("_save")) return "Update";
    if (toolKey.includes("_export")) return "Export";
    if (toolKey.includes("_summary")) return "Summary";
    if (toolKey.includes("_read")) return "Read";
    return mode === "read" ? "Read"
      : mode === "delete" ? "Delete"
      : mode === "link" ? "Link"
      : mode === "move" ? "Move"
      : mode === "run" ? "Run"
      : mode === "create" ? "Create"
      : mode === "write" ? "Write"
      : "Work";
  }

  function isOkfToolKey(toolKey) {
    return /^project_(knowledge|okf)_/.test(toolKey || "")
      || /^organization_(knowledge|okf)_/.test(toolKey || "");
  }

  function isAssistantResponseActivity(activity) {
    const toolKey = activityToolKey(activity);
    const raw = norm(activity && (activity.messageType || activity.toolName || activity.name));
    return toolKey === "assistant_response"
      || toolKey === "assistant_thinking"
      || toolKey === "assistant-delta"
      || raw.includes("agentmessage/delta")
      || raw.includes("agent_message/delta")
      || raw.includes("reasoning/summarypartadded")
      || raw.includes("reasoning/summarytextdelta");
  }

  function speechTextFromActivity(activity) {
    return clean(activity && (activity.streamText || activity.lastSpeechText));
  }

  function speechMetaFromActivity(activity) {
    if (!activity || !window.actionCenterActivityMeta) return null;
    return window.actionCenterActivityMeta({
      toolName: activity.lastSpeechToolName || activity.messageType || activity.toolName || "assistant_response",
      messageType: activity.lastSpeechToolName || activity.messageType || activity.toolName || "assistant_response",
      message: speechTextFromActivity(activity),
    });
  }

  function toOfficeWorkActivity(item) {
    const event = item && item.event || {};
    const semantic = item && item.semanticEvent || {};
    const source = semantic.sourceIdentifiers || {};
    return {
      toolName: item && (item.toolName || item.name || item.lastSpeechToolName) || event.latestMessageKind || source.latestMessageKind || "",
      messageType: item && (item.messageType || item.lastSpeechToolName) || event.latestMessageKind || source.latestMessageKind || "",
      message: item && (item.message || item.streamText || item.lastSpeechText) || event.latestMessageText || "",
      streamText: item && item.streamText || "",
      lastSpeechText: item && item.lastSpeechText || "",
      lastSpeechToolName: item && item.lastSpeechToolName || "",
      changedAt: item && (item.updatedAt || item.lastEventAt || (item.timing && item.timing.updatedAt)) || event.latestMessageAt || "",
      command: item && item.command || "",
    };
  }

  function cleanSpeechText(value) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text || text.length > 500) return "";
    if (/https?:\/\//i.test(text) || /(authorization|credential|secret|password|api[_-]?key|bearer\s)/i.test(text)) return "";
    if (/```|^\s*[{[]/.test(text)) return "";
    return text;
  }

  function speechTextFromOfficeWork(item) {
    if (!item) return "";
    const event = item.event || {};
    const activity = toOfficeWorkActivity(item);
    const role = norm(event.latestMessageRole || item.latestMessageRole);
    const kind = norm(activity.messageType || activity.toolName);
    const text = cleanSpeechText(activity.streamText || activity.lastSpeechText || event.latestMessageText);
    if (!text) return "";
    if (isAssistantResponseActivity(activity)) return text.length > 90 ? `${text.slice(0, 87).trimEnd()}...` : text;
    if (kind.includes("tool") || kind.includes("debug") || kind.includes("internal") || kind.includes("payload") || kind.includes("log") || kind.includes("function_call")) return "";
    if (!["assistant", "employee", "ai", "system-assistant"].includes(role)) return "";
    return text.length > 90 ? `${text.slice(0, 87).trimEnd()}...` : text;
  }

  function factoryAnimationMeta(activity, category, subject) {
    if (!activity || isAssistantResponseActivity(activity)) return null;
    const key = activityToolKey(activity);
    if (!key && !["tool_call", "command_execution", "generic_tool_work"].includes(norm(category))) return null;
    const meta = window.actionCenterActivityMeta ? window.actionCenterActivityMeta(activity) : null;
    const text = `${key} ${activity.toolName || ""} ${activity.messageType || ""} ${activity.message || ""} ${subject || ""}`.toLowerCase();
    const has = (...parts) => parts.some((part) => text.includes(part));
    let mode = key === "exec_command" || key.includes("command") || key.includes("shell")
      ? "run"
      : toolActionMode(key);
    let zone = "workshop";
    let anim = "hammer";
    const okfTool = isOkfToolKey(key);
    if (okfTool) {
      zone = "archive_shelves";
      anim = mode === "read" || key.endsWith("_list") || key.endsWith("_get") || key.endsWith("_export") ? "read" : "write";
      if (mode !== "read") mode = "write";
    } else if (has("websearch", "web_search", "search_query", "image_query", "browse", "browser.search") || key.endsWith("_search") || has("search_")) {
      zone = "archive_shelves"; anim = "search";
    } else if (mode === "run") {
      zone = "steamworks"; anim = "stoke";
    } else if (mode === "write" || mode === "create") {
      zone = "records"; anim = "write";
    } else if (mode === "delete") {
      zone = "workshop"; anim = "hammer";
    } else if (mode === "link" || has("attach", "upload")) {
      zone = "mailroom"; anim = "sort";
    } else if (mode === "read") {
      zone = "archive_shelves"; anim = "read";
    } else if (has("email", "mailbox", "message")) {
      zone = "mailroom"; anim = "sort";
    } else if (has("ticket")) {
      zone = "steamworks"; anim = "stoke";
    } else if (has("sketch", "diagram", "wireframe", "canvas", "mock")) {
      zone = "atelier"; anim = "paint";
    }
    const label = meta && meta.label ? meta.label : toolActionLabel(key, mode);
    return { key, mode, label, zone, anim, subject: clean(subject), chip: toolActionLabel(key, mode).toUpperCase() };
  }

  window.ActionCenterActivity = Object.freeze({
    activityToolKey,
    toolActionMode,
    toolActionLabel,
    isAssistantResponseActivity,
    speechTextFromActivity,
    speechMetaFromActivity,
    toOfficeWorkActivity,
    cleanSpeechText,
    speechTextFromOfficeWork,
    factoryAnimationMeta,
  });
})();
